import { useEffect, useMemo, useState } from "react";
import { Field, Modal, Spinner } from "../components/ui";
import { api, errorMessage, type CompileWordCount, type Project } from "../lib/api";
import { toast } from "../lib/app";

interface Sample {
  at: number;
  words: number;
}

const CHART_DAYS = 30;
const fmt = (n: number) => n.toLocaleString();

/** The total at the end of each of the last `days` local days, carrying the last sample forward. */
export function dailyTotals(history: Sample[], days: number, today = new Date()): Array<{ day: Date; words: number | null }> {
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  let i = 0;
  let last: number | null = null;
  while (i < history.length && history[i].at < start.getTime()) last = history[i++].words;
  const out: Array<{ day: Date; words: number | null }> = [];
  for (let d = 0; d < days; d++) {
    const day = new Date(start);
    day.setDate(start.getDate() + d);
    const next = new Date(day);
    next.setDate(day.getDate() + 1);
    while (i < history.length && history[i].at < next.getTime()) last = history[i++].words;
    out.push({ day, words: last });
  }
  return out;
}

/** Words written since local midnight, or since the first sample today if there's none before it. */
export function wordsToday(history: Sample[], current: number, today = new Date()): number | null {
  const midnight = new Date(today);
  midnight.setHours(0, 0, 0, 0);
  const before = history.filter((s) => s.at < midnight.getTime());
  const baseline = before.length ? before[before.length - 1] : history.find((s) => s.at >= midnight.getTime());
  return baseline ? current - baseline.words : null;
}

export function WordProgressDialog({
  project,
  words,
  canEdit,
  compiling,
  onCompile,
  onProject,
  onClose,
}: {
  project: Project;
  words: CompileWordCount | null;
  canEdit: boolean;
  compiling: boolean;
  onCompile: () => void;
  onProject: (p: Project) => void;
  onClose: () => void;
}) {
  const [history, setHistory] = useState<Sample[] | null>(null);
  const [target, setTarget] = useState(project.wordTarget ? String(project.wordTarget) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ history: Sample[] }>(`/api/projects/${project.id}/wordcount/history?days=${CHART_DAYS + 1}`)
      .then((r) => setHistory(r.history))
      .catch(() => setHistory([]));
  }, [project.id, words?.total]);

  const days = useMemo(() => dailyTotals(history ?? [], CHART_DAYS), [history]);
  const today = words && history ? wordsToday(history, words.total) : null;
  const goal = project.wordTarget;

  async function saveTarget() {
    const n = target.trim() ? Number(target.replace(/[,\s]/g, "")) : 0;
    if (!Number.isInteger(n) || n < 0) {
      toast("Enter a whole number of words", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, { wordTarget: n });
      onProject(r.project);
      setTarget(n ? String(n) : "");
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Word progress" onClose={onClose} width={560}>
      {!words ? (
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            {compiling ? "Counting after this compile finishes…" : "Words are counted with texcount each time the project compiles."}
          </p>
          {canEdit && !compiling && (
            <div>
              <button className="btn" onClick={onCompile}>
                Recompile now
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="stack">
          <div className="wc-hero">
            <div>
              <span className="wc-big">{fmt(words.total)}</span> <span className="muted">words in {project.rootDoc}</span>
            </div>
            {today !== null && today !== 0 && (
              <span className={`chip ${today > 0 ? "chip-ok" : ""}`}>
                {today > 0 ? "+" : "−"}
                {fmt(Math.abs(today))} today
              </span>
            )}
          </div>

          {goal > 0 && (
            <div>
              <div className="wc-progress" role="progressbar" aria-valuemin={0} aria-valuemax={goal} aria-valuenow={words.total}>
                <div style={{ width: `${Math.min(100, (words.total / goal) * 100)}%` }} />
              </div>
              <div className="muted small" style={{ marginTop: 4 }}>
                {words.total >= goal
                  ? `Target of ${fmt(goal)} reached${words.total > goal ? `, ${fmt(words.total - goal)} over` : ""}`
                  : `${Math.floor((words.total / goal) * 100)}% of ${fmt(goal)} · ${fmt(goal - words.total)} to go`}
              </div>
            </div>
          )}

          <div className="wc-grid">
            <Stat label="Text" value={words.text} />
            <Stat label="Headings" value={words.headers} />
            <Stat label="Captions" value={words.captions} />
            <Stat label="Figures & tables" value={words.floats} />
            <Stat label="Inline maths" value={words.mathInline} />
            <Stat label="Displayed maths" value={words.mathDisplay} />
          </div>

          {words.problems.length > 0 && (
            <div className="alert alert-warn">
              Some files weren't counted: {words.problems.join("; ")}
            </div>
          )}

          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>
              Last {CHART_DAYS} days
            </div>
            {history === null ? <Spinner /> : <WordChart days={days} />}
          </div>

          {words.sections.length > 1 && <Sections sections={words.sections} />}
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: 16 }}>
          <Field label="Target" hint="Leave empty for no target.">
            <div className="row">
              <input
                className="grow"
                inputMode="numeric"
                placeholder="e.g. 80000"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveTarget()}
              />
              <button className="btn" disabled={saving || target.trim() === (goal ? String(goal) : "")} onClick={saveTarget}>
                {saving ? <Spinner /> : "Save"}
              </button>
            </div>
          </Field>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="wc-stat">
      <div className="wc-stat-value">{fmt(value)}</div>
      <div className="muted small">{label}</div>
    </div>
  );
}

function WordChart({ days }: { days: Array<{ day: Date; words: number | null }> }) {
  const values = days.map((d) => d.words ?? 0);
  const max = Math.max(...values);
  if (max === 0) return <div className="muted small">No history yet. It builds up as the project compiles.</div>;
  const w = 100 / days.length;
  return (
    <svg className="wc-chart" viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label="Word count per day">
      {days.map((d, i) => {
        if (d.words === null) return null;
        const h = Math.max(0.6, (d.words / max) * 38);
        return (
          <rect key={i} x={i * w + w * 0.15} y={40 - h} width={w * 0.7} height={h} className={i === days.length - 1 ? "today" : undefined}>
            <title>{`${d.day.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}: ${fmt(d.words)} words`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** Sections in document order; a top-level row's count includes the sections under it. */
export function sectionRows(sections: CompileWordCount["sections"]) {
  const topLevel = sections.some((s) => s.kind === "part") ? "part" : sections.some((s) => s.kind === "chapter") ? "chapter" : "section";
  const rows = sections.map((s) => ({ ...s, top: s.kind === topLevel || s.kind === "top", words: s.text + s.headers + s.captions }));
  let parent: (typeof rows)[number] | null = null;
  for (const r of rows) {
    if (r.top) parent = r.kind === "top" ? null : r;
    else if (parent) parent.words += r.words;
  }
  return rows;
}

function Sections({ sections }: { sections: CompileWordCount["sections"] }) {
  const rows = sectionRows(sections);
  const max = Math.max(...rows.map((r) => r.words), 1);
  return (
    <table className="table wc-section-table">
      <thead>
        <tr>
          <th>Section</th>
          <th style={{ textAlign: "right" }}>Words</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className={r.top ? "" : "nested"}>
              <div className="wc-section-title">
                {r.kind === "top" ? <i className="muted">Before the first heading</i> : r.top ? <b>{r.title}</b> : r.title}
              </div>
              <div className="wc-section-bar" style={{ width: `${(r.words / max) * 100}%` }} />
            </td>
            <td style={{ textAlign: "right" }} className="mono">
              {fmt(r.words)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
